// File: /src/pages/Reports.js
import React, { useEffect, useState } from 'react';
import './Reports.css';
import { FaCheckCircle, FaCertificate, FaBan, FaWallet } from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend } from 'chart.js';
import * as XLSX from 'xlsx';
import { getAllVCs } from '../../api/ssiAPI';
import { getProductsByUserId } from '../../api/productApi';
import { getMe } from '../../api/loginAPI';
import { getWalletMappingsByIssuer } from '../../api/wallet-mappingAPI';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

function Reports() {
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [allCertificates, setAllCertificates] = useState([]);
  const [allWallets, setAllWallets] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    async function fetchData() {
      const userData = await getMe();
      setUser(userData);
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function fetchProducts() {
      if (user) {
        const res = await getProductsByUserId(user.Id);
        setProducts(res);
      }
    }
    fetchProducts();
  }, [user]);

  useEffect(() => {
    if (products.length) {
      const hasCert = products.some(p => p.productName === 'Digital Certificate');
      const hasWallet = products.some(p => p.productName === 'EarthID Wallet');
      if (hasCert) getAllVCs(user.Id).then(res => setAllCertificates(res?.response || []));
      if (hasWallet) getWalletMappingsByIssuer(user.Id).then(res => setAllWallets(res || []));
    }
  }, [products]);

  const filterByDate = (list) => {
    return list.filter(item => {
      const issuedDate = new Date(item?.vc?.issuanceDate || item?.issuanceDate);
      const from = startDate ? new Date(startDate) : null;
      const to = endDate ? new Date(endDate) : null;
      return (!from || issuedDate >= from) && (!to || issuedDate <= to);
    });
  };

  const filteredCertificates = filterByDate(allCertificates);
  const filteredWallets = filterByDate(allWallets);

  const certChartData = {
    labels: filteredCertificates.map((cert) => {
      const date = new Date(cert.vc?.issuanceDate);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    }),
    datasets: [{
      label: 'Issued Certificates',
      data: filteredCertificates.map(() => 1),
      borderColor: '#5f0fa5',
      backgroundColor: 'rgba(95, 15, 165, 0.2)',
      tension: 0.4,
      fill: true,
    }]
  };

  const walletChartData = {
    labels: filteredWallets.map((w) => {
      const date = new Date(w.created_at);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    }),
    datasets: [{
      label: 'Issued Wallets',
      data: filteredWallets.map(() => 1),
      borderColor: '#0fa58a',
      backgroundColor: 'rgba(15, 165, 138, 0.2)',
      tension: 0.4,
      fill: true,
    }]
  };

  const downloadReport = (type = 'vc') => {
    const data = type === 'vc' ? filteredCertificates : filteredWallets;
    const rows = data.map((item, index) => ({
      ID: index + 1,
      Name: item?.vc?.credentialSubject?.[0]?.subjectName || item?.subjectName || 'N/A',
      DID: item?.vc?.credentialSubject?.[0]?.subjectDid || item?.did || 'N/A',
      Status: 'Active',
      'Issuance Date': new Date(item?.vc?.issuanceDate || item?.issuanceDate).toLocaleDateString(),
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, type === 'vc' ? 'Certificates' : 'Wallets');
    XLSX.writeFile(workbook, `${type === 'vc' ? 'certificates' : 'wallets'}_report.xlsx`);
  };

  return (
    <div className="page reports-layout">
      <h2 className="reports-title">Reports</h2>

      {/* Digital Certificate Report */}
      {products.some(p => p.productName === 'Digital Certificate') && (
        <>
          <div className="section-reports">
            <h3 className="section-heading">Digital Certificates</h3>
            <div className="card-row">
              <div className="report-card issued">
                <FaCertificate className="card-icon" />
                <div>
                  <h3>{filteredCertificates.length}</h3>
                  <p>Issued</p>
                </div>
              </div>
              <div className="report-card active">
                <FaCheckCircle className="card-icon" />
                <div>
                  <h3>{filteredCertificates.length}</h3>
                  <p>Active</p>
                </div>
              </div>
              <div className="report-card revoked">
                <FaBan className="card-icon" />
                <div>
                  <h3>0</h3>
                  <p>Revoked</p>
                </div>
              </div>
            </div>

            <Line data={certChartData} height={100} />

            <div className="filters">
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              <button className="gradient-btn" onClick={() => downloadReport('vc')}>Download Certificate Report</button>
            </div>

            <div className="table-section">
              <table>
                <thead>
                  <tr>
                    <th>Sr No</th>
                    <th>Name</th>
                    <th>DID</th>
                    <th>Status</th>
                    <th>Issuance Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCertificates.map((cert, index) => (
                    <tr key={cert._id || index}>
                      <td>{index + 1}</td>
                      <td>{cert.vc?.credentialSubject?.[0]?.subjectName || 'N/A'}</td>
                      <td>{cert.vc?.credentialSubject?.[0]?.subjectDid || 'N/A'}</td>
                      <td>Active</td>
                      <td>{new Date(cert.vc?.issuanceDate).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <hr className="section-divider" />

      {/* Wallet Report */}
      {products.some(p => p.productName === 'EarthID Wallet') && (
        <>
          <div className="section-reports">
            <h3 className="section-heading">EarthID Wallets</h3>
            <div className="card-row">
              <div className="report-card issued">
                <FaWallet className="card-icon" />
                <div>
                  <h3>{filteredWallets.length}</h3>
                  <p>Issued</p>
                </div>
              </div>
              <div className="report-card active">
                <FaCheckCircle className="card-icon" />
                <div>
                  <h3>{filteredWallets.length}</h3>
                  <p>Active</p>
                </div>
              </div>
              <div className="report-card revoked">
                <FaBan className="card-icon" />
                <div>
                  <h3>0</h3>
                  <p>Revoked</p>
                </div>
              </div>
            </div>

            <Line data={walletChartData} height={100} />

            <div className="filters">
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              <button className="gradient-btn" onClick={() => downloadReport('wallet')}>Download Wallet Report</button>
            </div>

            <div className="table-section">
              <table>
                <thead>
                  <tr>
                    <th>Sr No</th>
                    <th>Name</th>
                    <th>EarthID</th>
                    <th>Status</th>
                    <th>Issuance Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWallets.map((wallet, index) => (
                    <tr key={wallet._id || index}>
                      <td>{index + 1}</td>
                      <td>{wallet.name || 'N/A'}</td>
                      <td>{wallet.wallet_id || 'N/A'}</td>
                      <td>Active</td>
                      <td>{new Date(wallet.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Reports;
